// Registro fotografico y QR WhatsApp para PDFs
import { C, M, T, CONTENT_W } from '../theme.js';
import { fitEnCaja } from '../helpers.js';

export async function dibujarQRWhatsApp(doc, { x, y, telefono, mensaje = '', titulo = '¿ESTÁ DE ACUERDO CON ESTE PRESUPUESTO?', nroDoc = '' }) {
    try {
        const QRCode = await import('qrcode').catch(() => null);
        if (!QRCode) return dibujarQRPlaceholder(doc, x, y);

        const waUrl  = `https://wa.me/${telefono.replace(/\D/g,'')}${mensaje ? `?text=${encodeURIComponent(mensaje)}` : ''}`;
        const dataUrl = await QRCode.default.toDataURL(waUrl, { width: 140, margin: 1, color: { dark: '#0D2B5B', light: '#ffffff' } });

        const QR_W  = 28;
        const QR_H  = 28;

        // Card de fondo
        const cardW = CONTENT_W + 4;
        const cardH = 38;
        doc.setFillColor(...C.grayLight);
        doc.setDrawColor(...C.grayBorder);
        doc.setLineWidth(0.25);
        doc.roundedRect(M - 2, y, cardW, cardH, 2, 2, 'FD');

        // QR
        doc.addImage(dataUrl, 'PNG', M + 2, y + 5, QR_W, QR_H);
        doc.setDrawColor(...C.grayBorder);
        doc.setLineWidth(0.15);
        doc.roundedRect(M + 2, y + 5, QR_W, QR_H, 1, 1, 'S');

        // Texto
        doc.setFontSize(T.sm);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(...C.dark);
        doc.text(titulo, M + QR_W + 8, y + 12);

        doc.setFontSize(T.xxs);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(...C.grayText);
        doc.text('Respondé por WhatsApp para coordinar el servicio.', M + QR_W + 8, y + 18);
        if (nroDoc) doc.text(`Nro. presupuesto: ${nroDoc}`, M + QR_W + 8, y + 23);

        doc.setFontSize(T.xxs);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(...C.green);
        doc.text('WhatsApp directo', M + QR_W + 8, y + 30);

        return y + cardH + 6;
    } catch {
        return dibujarQRPlaceholder(doc, x, y);
    }
}

function dibujarQRPlaceholder(doc, x, y) {
    doc.setFillColor(...C.grayBg);
    doc.setDrawColor(...C.grayBorder);
    doc.setLineWidth(0.2);
    doc.roundedRect(M - 2, y, CONTENT_W + 4, 38, 2, 2, 'FD');
    doc.setFontSize(T.xs);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(...C.grayText);
    doc.text('[QR WhatsApp]', M + 4, y + 20);
    return y + 44;
}

// Alto total que ocupa el registro fotográfico (título + fotos + rótulos), para decidir
// antes de dibujar si conviene la versión compacta y que el PDF entre en una hoja.
export function altoRegistroFotografico(fotoA, fotoD, compacto = false) {
    const esHz = (f) => f && f.w && f.h && f.w > f.h;
    const alto = (f) => !f ? 0 : compacto ? 56 : (esHz(f) ? 66 : 74);
    if (!fotoA && !fotoD) return 0;
    return 5 + Math.max(alto(fotoA), alto(fotoD)) + 10;
}

export function dibujarRegistroFotografico(doc, { y, fotoA = null, fotoD = null, esPresupuesto = false, compacto = false }) {
    if (!fotoA && !fotoD) return y;

    // Tamaños normales y compactos por orientación
    const esHz = (foto) => foto && foto.w && foto.h && foto.w > foto.h;
    const dims = (foto) => {
        if (compacto) return esHz(foto) ? { bw: 74, bh: 56 } : { bw: 42, bh: 56 };
        return esHz(foto) ? { bw: 88, bh: 66 } : { bw: 56, bh: 74 };
    };

    doc.setFontSize(T.label);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(...C.navy);
    doc.text(esPresupuesto ? 'FOTOGRAFÍAS DEL EQUIPO' : 'REGISTRO FOTOGRÁFICO', M, y);
    y += 5;

    const dibujarCaja = (foto, x, bw, bh, label) => {
        const { w: fW, h: fH } = fitEnCaja(foto?.w, foto?.h, bw, bh);
        const offX = (bw - fW) / 2;
        const offY = (bh - fH) / 2;
        doc.setFillColor(220, 220, 225);
        doc.roundedRect(x + 1, y + 1, bw, bh, 2, 2, 'F');
        if (foto) {
            try { doc.addImage(foto.data, foto.format, x + offX, y + offY, fW, fH); } catch {}
        } else {
            doc.setFillColor(...C.grayBg);
            doc.roundedRect(x, y, bw, bh, 2, 2, 'F');
        }
        doc.setDrawColor(...C.grayBorder);
        doc.setLineWidth(0.2);
        doc.roundedRect(x, y, bw, bh, 2, 2, 'S');
        doc.setFontSize(T.xxs);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(...C.grayText);
        doc.text(label, x + bw / 2, y + bh + 4.5, { align: 'center' });
    };

    if (fotoA && fotoD) {
        const lA = esPresupuesto ? 'FOTO 1' : 'ESTADO INICIAL';
        const lD = esPresupuesto ? 'FOTO 2' : 'ESTADO FINAL';
        const bA = dims(fotoA);
        const bD = dims(fotoD);
        const totalW = bA.bw + 6 + bD.bw;
        const xBase  = M + (CONTENT_W - totalW) / 2;
        dibujarCaja(fotoA, xBase,              bA.bw, bA.bh, lA);
        dibujarCaja(fotoD, xBase + bA.bw + 6, bD.bw, bD.bh, lD);
        return y + Math.max(bA.bh, bD.bh) + 10;
    } else {
        const foto = fotoA || fotoD;
        const b = dims(foto);
        const xCentro = M + (CONTENT_W - b.bw) / 2;
        dibujarCaja(foto, xCentro, b.bw, b.bh, esPresupuesto ? 'FOTO DEL EQUIPO' : (fotoA ? 'ESTADO INICIAL' : 'ESTADO FINAL'));
        return y + b.bh + 10;
    }
}

// ── Fotos del trabajo (14-oct-2026): hasta 4, sin "antes/después", en una sola fila ──
const GAP_FT = 4;
function cajasFotosTrabajo(n, compacto) {
    const bw = Math.min(n <= 2 ? 70 : 60, (CONTENT_W - (n - 1) * GAP_FT) / n);
    const bh = Math.min(bw * 0.75, compacto ? 30 : 52);
    return { bw, bh };
}
export function altoFotosTrabajo(fotos, compacto = false) {
    const n = (fotos || []).filter(Boolean).length;
    if (!n) return 0;
    return 5 + cajasFotosTrabajo(n, compacto).bh + 6;
}
export function dibujarFotosTrabajo(doc, { y, fotos, compacto = false, titulo = 'FOTOS DEL TRABAJO' }) {
    const lista = (fotos || []).filter(Boolean).slice(0, 4);
    if (!lista.length) return y;
    const { bw, bh } = cajasFotosTrabajo(lista.length, compacto);
    doc.setFontSize(T.label);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(...C.navy);
    doc.text(titulo, M, y);
    y += 5;
    const totalW = lista.length * bw + (lista.length - 1) * GAP_FT;
    let x = M + (CONTENT_W - totalW) / 2;
    lista.forEach(foto => {
        const { w: fW, h: fH } = fitEnCaja(foto.w, foto.h, bw, bh);
        doc.setFillColor(...C.grayBg);
        doc.roundedRect(x, y, bw, bh, 2, 2, 'F');
        try { doc.addImage(foto.data, foto.format, x + (bw - fW) / 2, y + (bh - fH) / 2, fW, fH); } catch { /* foto rota: queda la caja */ }
        doc.setDrawColor(...C.grayBorder);
        doc.setLineWidth(0.2);
        doc.roundedRect(x, y, bw, bh, 2, 2, 'S');
        x += bw + GAP_FT;
    });
    return y + bh + 6;
}

// ── Galería (18-oct-2026): antes, después y las extras juntas, hasta 4 por fila ──────────────
// Antes: antes/después grandes de a 2 y las extras en otra fila, así con 3 o 4 fotos el PDF
// pasaba a una segunda hoja casi vacía. Las cajas son verticales (los dispensers son altos):
// con 4 fotos, cada una ocupa un cuarto del ancho.
const POR_FILA = 4;
const GAP_GAL = 4;
const ROTULO_GAL = 7;
// alto: 58 normal, 46 mediano, 36 chico (o true = 42, el compacto de antes)
export const ALTOS_GALERIA = [58, 46, 36];
const altoDe = (a) => (a === true ? 42 : a === false || a == null ? 58 : a);
function cajaGaleria(n, alto) {
    const enFila = Math.min(n, POR_FILA);
    const bh = altoDe(alto);
    const bw = Math.min(enFila <= 2 ? bh : bh * 0.8, (CONTENT_W - (enFila - 1) * GAP_GAL) / enFila);
    return { bw, bh };
}
export function altoGaleriaFotos(fotos, alto = 58) {
    const n = (fotos || []).filter(f => f?.foto).length;
    if (!n) return 0;
    const filas = Math.ceil(n / POR_FILA);
    return 5 + filas * (cajaGaleria(n, alto).bh + ROTULO_GAL) + 2;
}
// fotos: [{ foto, label }]
export function dibujarGaleriaFotos(doc, { y, fotos, compacto = 58, titulo = 'REGISTRO FOTOGRÁFICO', pageH = 297, alSaltar = null }) {
    const lista = (fotos || []).filter(f => f?.foto);
    if (!lista.length) return y;
    const { bw, bh } = cajaGaleria(lista.length, compacto);
    if (titulo) {   // sin título: lo dibuja quien llama (diseño nuevo, 18-oct-2026)
        doc.setFontSize(T.label);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(...C.navy);
        doc.text(titulo, M, y);
        y += 5;
    }
    for (let i = 0; i < lista.length; i += POR_FILA) {
        const fila = lista.slice(i, i + POR_FILA);
        if (i > 0 && alSaltar && y + bh + ROTULO_GAL > pageH - 25) y = alSaltar();
        const totalW = fila.length * bw + (fila.length - 1) * GAP_GAL;
        let x = M + (CONTENT_W - totalW) / 2;
        const yFila = y;
        for (const { foto, label } of fila) {
            const { w: fW, h: fH } = fitEnCaja(foto.w, foto.h, bw, bh);
            doc.setFillColor(...C.grayBg);
            doc.roundedRect(x, yFila, bw, bh, 2, 2, 'F');
            try { doc.addImage(foto.data, foto.format, x + (bw - fW) / 2, yFila + (bh - fH) / 2, fW, fH); } catch { /* foto rota: queda la caja */ }
            doc.setDrawColor(...C.grayBorder);
            doc.setLineWidth(0.2);
            doc.roundedRect(x, yFila, bw, bh, 2, 2, 'S');
            if (label) {
                doc.setFontSize(T.xxs);
                doc.setFont(undefined, 'bold');
                doc.setTextColor(...C.grayText);
                doc.text(label, x + bw / 2, yFila + bh + 4.5, { align: 'center' });
            }
            x += bw + GAP_GAL;
        }
        y += bh + ROTULO_GAL;
    }
    return y + 2;
}
